import { Modal } from '~/components/ui/Modal';
import { X, AlertTriangle } from 'lucide-react';
import { useNavigate } from '@remix-run/react';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface StorageExceededModalProps {
  isOpen: boolean;
  onClose: () => void;
  setIsProUpgradeModalOpen: (isOpen: boolean) => void;
}

export function StorageExceededModal({ isOpen, setIsProUpgradeModalOpen, onClose }: StorageExceededModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleUpgrade = () => {
    setIsProUpgradeModalOpen(true);
    onClose();
  };

  const handleGoToMyProjects = () => {
    navigate('/settings/project-settings');
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="rounded-lg bg-dualite-background-secondary p-0 w-[480px] max-w-[90vw] text-dualite-text-primary shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dualite-border-primary">
          <div className="flex items-center gap-3">
            {' '}
            {/* Increased gap for better spacing */}
            <AlertTriangle size={24} className="text-red-500" />
            <h2 className="text-lg font-medium text-dualite-text-primary">
              {t('modals.storage.title', 'Storage has Exceeded')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--dualite-text-tertiary)] hover:text-[var(--dualite-text-primary)] p-1 rounded-md hover:bg-[var(--dualite-background-cta)] transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 text-sm text-[var(--dualite-text-secondary)] leading-relaxed">
          {' '}
          {/* Added leading-relaxed */}
          <p>
            {t(
              'modals.storage.message',
              'Your storage has exceeded. You need to delete projects, or upgrade to a higher plan.',
            )}
          </p>
          <div className="flex justify-start space-x-3 pt-4 rounded-b-lg">
            <button
              onClick={handleUpgrade}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-dualite-primary-start to-dualite-primary-end rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dualite-primary-start focus:ring-offset-dualite-background-tertiary"
            >
              {t('modals.storage.upgrade', 'Upgrade')}
            </button>
            <button
              onClick={handleGoToMyProjects}
              className="px-4 py-2 text-sm font-medium text-dualite-text-cta bg-dualite-background-cta rounded-md hover:bg-dualite-background-cta transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dualite-background-cta focus:ring-offset-dualite-border-secondary"
            >
              {t('modals.storage.goToProjects', 'Go to My Projects')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
